/**
 * =============================================================================
 * WlTenantCourtesyButton
 *
 * Botão do header do modal de tenant do painel WL para marcar/desmarcar o
 * tenant como "Cortesia". Cortesia é uma flag cirúrgica que exclui o tenant
 * dos KPIs financeiros WL — NÃO altera plano, assinatura ou add-ons. O
 * operador continua responsável por aplicar 100% de desconto manualmente
 * na recorrência do tenant (procedimento existente).
 *
 * Comportamento:
 *   - Se `is_courtesy=false` → botão "Marcar Cortesia". Ao clicar, abre um
 *     Dialog pedindo o motivo (opcional). Confirma → chama wl_set_tenant_courtesy.
 *   - Se `is_courtesy=true` → renderiza badge "Cortesia" + botão de remoção.
 *   - Visível apenas para operadores com permissão `manage` em `wl.tenants`.
 *
 * @version 1.1.0
 * RELEASE: 2026-10-04 v1.1.0 - Modo `menuMode` + `openSignal`: botão some e a ação é disparada
 *          pelo menu "Ações" do modal do tenant (mesma lógica, dialogs mantidos aqui).
 * RELEASE: 2026-07-22 v1.0.0 - Versão inicial.
 * =============================================================================
 */
import { useEffect, useRef, useState } from "react";
import { Gift, Loader2, X } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";
import { useWlPermissions } from "@/contexts/WlPermissionsContext";
import { describeWlError } from "@/hooks/wl/useWlErrorToast";
import {
  useWlTenantCourtesy,
  useWlSetTenantCourtesy,
} from "@/hooks/wl/useWlTenantCourtesy";
import { cn } from "@/lib/utils";

interface Props {
  tenantId: string | null;
  /** Renderiza apenas o badge (usado ao lado do título quando ativo). */
  badgeOnly?: boolean;
  /** v1.1.0: oculta o botão; a ação é acionada externamente via openSignal (menu Ações). */
  menuMode?: boolean;
  /** v1.1.0: cada incremento dispara a ação atual (marcar abre dialog / remover confirma). */
  openSignal?: number;
}

export function WlTenantCourtesyButton({ tenantId, badgeOnly = false, menuMode = false, openSignal = 0 }: Props) {
  const { toast } = useToast();
  const { canEdit } = useWlPermissions();
  const { confirmDialog, ConfirmDialogComponent } = useConfirmDialog();

  const { data, isLoading } = useWlTenantCourtesy(tenantId);
  const setCourtesy = useWlSetTenantCourtesy(tenantId);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [reason, setReason] = useState("");

  // Reseta o campo motivo ao abrir/fechar
  useEffect(() => {
    if (dialogOpen) setReason("");
  }, [dialogOpen]);

  const isCourtesy = !!data?.is_courtesy;
  const canManage = canEdit("wl.tenants");

  // v1.1.0: ação disparada pelo menu externo (ref atualizada após os handlers)
  const actionRef = useRef<() => void>(() => {});
  useEffect(() => {
    if (menuMode && openSignal > 0) actionRef.current();
  }, [menuMode, openSignal]);

  // Sem permissão + sem cortesia ativa → não renderiza nada
  if (!tenantId) return null;
  if (!canManage && !isCourtesy) return null;

  // Handler: confirmar ativação
  const handleActivate = async () => {
    try {
      await setCourtesy.mutateAsync({ isCourtesy: true, reason: reason.trim() || null });
      toast({
        title: "Tenant marcado como cortesia",
        description: "Este tenant não entrará mais nos KPIs financeiros do painel WL.",
      });
      setDialogOpen(false);
    } catch (err) {
      toast({
        title: "Falha ao marcar cortesia",
        description: describeWlError(err),
        variant: "destructive",
      });
    }
  };

  // Handler: remover cortesia
  const handleRemove = async () => {
    const ok = await confirmDialog({
      title: "Remover cortesia deste tenant?",
      description:
        "O tenant voltará a ser contado nos KPIs financeiros do painel WL (MRR, ARR, add-ons, séries).",
      variant: "warning",
      confirmText: "Remover cortesia",
      cancelText: "Cancelar",
    });
    if (!ok) return;
    try {
      await setCourtesy.mutateAsync({ isCourtesy: false, reason: null });
      toast({
        title: "Cortesia removida",
        description: "O tenant voltou ao cálculo normal dos KPIs financeiros.",
      });
    } catch (err) {
      toast({
        title: "Falha ao remover cortesia",
        description: describeWlError(err),
        variant: "destructive",
      });
    }
  };

  // v1.1.0: ação contextual para o menu externo
  actionRef.current = isCourtesy ? () => { void handleRemove(); } : () => setDialogOpen(true);

  // Modo badge-only (usado ao lado do título quando quiser destacar)
  if (badgeOnly) {
    if (!isCourtesy) return null;
    const grantedAt = data?.courtesy_granted_at
      ? format(new Date(data.courtesy_granted_at), "dd/MM/yyyy", { locale: ptBR })
      : null;
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge
              variant="outline"
              className="h-5 text-[10px] font-medium bg-fuchsia-500/10 text-fuchsia-700 border-fuchsia-500/30 dark:text-fuchsia-300 cursor-help gap-1"
            >
              <Gift className="h-3 w-3" />
              Cortesia
            </Badge>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs">
            <div className="text-xs space-y-0.5">
              <div className="font-semibold">Tenant cortesia</div>
              {data?.courtesy_reason && (
                <div className="text-muted-foreground">Motivo: {data.courtesy_reason}</div>
              )}
              {grantedAt && (
                <div className="text-muted-foreground">Desde: {grantedAt}</div>
              )}
              {data?.granted_by_name && (
                <div className="text-muted-foreground">Por: {data.granted_by_name}</div>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Cortesia ativa: botão "Remover cortesia" (só operadores com manage)
  if (isCourtesy) {
    if (!canManage) return null;
    return (
      <>
        {!menuMode && <Button
          size="sm"
          variant="outline"
          className="gap-1.5 border-fuchsia-500/40 text-fuchsia-700 hover:bg-fuchsia-500/10 hover:text-fuchsia-800 dark:text-fuchsia-300"
          onClick={handleRemove}
          disabled={setCourtesy.isPending || isLoading}
          title="Remover flag de cortesia deste tenant"
        >
          {setCourtesy.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <X className="h-3.5 w-3.5" />
          )}
          Remover cortesia
        </Button>}
        {ConfirmDialogComponent}
      </>
    );
  }

  // Cortesia inativa: botão "Marcar cortesia" (abre dialog c/ motivo)
  return (
    <>
      {!menuMode && <Button
        size="sm"
        variant="outline"
        className="gap-1.5 border-fuchsia-500/40 text-fuchsia-700 hover:bg-fuchsia-500/10 hover:text-fuchsia-800 dark:text-fuchsia-300"
        onClick={() => setDialogOpen(true)}
        disabled={isLoading}
        title="Marcar tenant como cortesia (não entra nos KPIs financeiros)"
      >
        <Gift className="h-3.5 w-3.5" />
        Marcar Cortesia
      </Button>}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className={cn("h-4 w-4 text-fuchsia-600")} />
              Marcar tenant como cortesia
            </DialogTitle>
            <DialogDescription className="text-xs leading-relaxed pt-1">
              Este tenant deixa de ser contabilizado nos KPIs financeiros do painel WL
              (MRR, ARR, add-ons, séries e distribuições). O plano, assinatura e add-ons
              permanecem intactos — o desconto de 100% na recorrência deve ser aplicado
              manualmente pelo operador. Ação reversível a qualquer momento.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="courtesy-reason" className="text-xs">
              Motivo (opcional)
            </Label>
            <Textarea
              id="courtesy-reason"
              placeholder="Ex.: parceria estratégica; conta interna; período promocional…"
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 500))}
              rows={3}
              className="text-sm"
            />
            <p className="text-[10px] text-muted-foreground text-right">
              {reason.length}/500
            </p>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDialogOpen(false)}
              disabled={setCourtesy.isPending}
            >
              Cancelar
            </Button>
            <Button
              size="sm"
              className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
              onClick={handleActivate}
              disabled={setCourtesy.isPending}
            >
              {setCourtesy.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
              ) : (
                <Gift className="h-3.5 w-3.5 mr-1.5" />
              )}
              Confirmar cortesia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
